import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { userSession } from '../../services/localStorageService';
import PublicHeader from '../../components/navigation/PublicHeader';
import Button from '../../components/ui/Button';
import RoleSelectionCard from './components/RoleSelectionCard';
import StepIndicator from './components/StepIndicator';
import CandidateQualificationForm from './components/CandidateQualificationForm';
import EmployerVerificationForm from './components/EmployerVerificationForm';
import EmployerTermsOfService from './components/EmployerTermsOfService';
import ApplicationStatusPanel from './components/ApplicationStatusPanel';
import SuccessConfirmation from './components/SuccessConfirmation';
import { useLanguage } from '../../contexts/LanguageContext';
import { accessRequestService } from '../../services/accessRequestService';

const AccessRequest = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [step, setStep] = useState(1);
  const [selectedRole, setSelectedRole] = useState(null);
  const [formData, setFormData] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [isSubmitted, setIsSubmitted] = useState(false);

  const steps = [
    { id: 'role', label: t('accessRequest.title') },
    { id: 'qualification', label: t('accessRequest.qualificationTitle') },
    ...(selectedRole === 'employer' ? [{ id: 'terms', label: t('legal.employerTermsTitle') }] : []),
    { id: 'review', label: t('common.submit') },
  ];

  const candidateRequirements = [
    t('accessRequest.candidateReq1'),
    t('accessRequest.candidateReq2'),
    t('accessRequest.candidateReq3'),
  ];

  const employerRequirements = [
    t('accessRequest.employerReq1'),
    t('accessRequest.employerReq2'),
    t('accessRequest.employerReq3'),
  ];

  const handleRoleSelect = (role) => {
    setSelectedRole(role);
    
    // Set user session with role
    userSession?.set({
      id: `user_${Date.now()}`,
      userType: role,
      createdAt: new Date()?.toISOString()
    });
    
    setStep(2);
  };

  const handleFormChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const validateStep = () => {
    const newErrors = {};

    if (step === 1 && !selectedRole) {
      newErrors.role = 'Please select a role to continue';
      setErrors(newErrors);
      return false;
    }

    if (step === 2) {
      if (!formData?.fullName?.trim()) {
        newErrors.fullName = 'Full name is required';
      }
      if (!formData?.email?.trim()) {
        newErrors.email = 'Email address is required';
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(formData?.email)) {
        newErrors.email = 'Please enter a valid email address';
      }

      if (selectedRole === 'candidate') {
        if (!formData?.experience) {
          newErrors.experience = 'Experience range is required';
        }
        if (!formData?.industry) {
          newErrors.industry = 'Industry selection is required';
        }
        if (!formData?.seniority) {
          newErrors.seniority = 'Seniority level is required';
        }
        if (!formData?.language) {
          newErrors.language = 'Language preference is required';
        }
        if (!formData?.location) {
          newErrors.location = 'Location preference is required';
        }
        if (!formData?.bio?.trim()) {
          newErrors.bio = 'Professional bio is required';
        } else if (formData?.bio?.trim()?.length < 100) {
          newErrors.bio = 'Bio must be at least 100 characters';
        }
        if (formData?.hasLinkedIn) {
          if (!formData?.linkedinUrl?.trim()) {
            newErrors.linkedinUrl = 'LinkedIn URL is required';
          } else if (!/^https?:\/\/(www\.)?linkedin\.com\/in\/.+$/?.test(formData?.linkedinUrl)) {
            newErrors.linkedinUrl = 'Please enter a valid LinkedIn profile URL';
          }
        } else {
          if (!formData?.cvFile) {
            newErrors.cvFile = 'CV upload is required if you don\'t have LinkedIn';
          }
        }
        if (!formData?.experienceConfirmed) {
          newErrors.experienceConfirmed = 'Please confirm your experience';
        }
      } else {
        if (!formData?.companyName?.trim()) {
          newErrors.companyName = 'Company name is required';
        }
        if (!formData?.jobTitle?.trim()) {
          newErrors.jobTitle = 'Job title is required';
        }
        if (!formData?.companySize) {
          newErrors.companySize = 'Company size is required';
        }
        if (!formData?.industry) {
          newErrors.industry = 'Industry is required';
        }
        if (!formData?.hiringVolume) {
          newErrors.hiringVolume = 'Hiring volume is required';
        }
        if (!formData?.teamSize) {
          newErrors.teamSize = 'Team size is required';
        }
        if (!formData?.hrBudget) {
          newErrors.hrBudget = 'Annual HR budget is required';
        }
        if (!formData?.companyWebsite?.trim()) {
          newErrors.companyWebsite = 'Company website is required';
        }
        if (!formData?.hiringAuthorityConfirmed) {
          newErrors.hiringAuthorityConfirmed = 'Please confirm hiring authority';
        }
        if (!formData?.directHiringConfirmed) {
          newErrors.directHiringConfirmed = 'Please confirm direct hiring';
        }
        if (!formData?.subscriptionAcknowledged) {
          newErrors.subscriptionAcknowledged = 'Please acknowledge subscription model';
        }
      }
    }

    if (step === 3 && selectedRole === 'employer') {
      if (!formData?.termsAccepted) {
        newErrors.termsAccepted = 'Please accept the Terms of Service';
      }
      if (!formData?.hiringFeeAccepted) {
        newErrors.hiringFeeAccepted = 'Please accept the 20% hiring fee agreement';
      }
      if (!formData?.antiCircumventionAccepted) {
        newErrors.antiCircumventionAccepted = 'Please accept the anti-circumvention agreement';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleNext = () => {
    if (validateStep()) {
      setStep(step + 1);
    }
  };

  const handleBack = () => {
    if (step > 1) {
      setStep(step - 1);
    }
  };

  const handleSubmit = async () => {
    // Skip validation on review step - data already validated in previous steps
    setIsSubmitting(true);
    setErrors({}); // Clear any previous errors
    
    try {
      // Save application to Supabase
      const application = {
        fullName: formData?.fullName,
        email: formData?.email,
        role: selectedRole,
        bio: formData?.bio,
        experience: formData?.experience,
        industry: formData?.industry,
        seniority: formData?.seniority,
        language: formData?.language,
        location: formData?.location,
        companyName: formData?.companyName,
        companyWebsite: formData?.companyWebsite,
        linkedinUrl: formData?.linkedinUrl
      };
      
      console.log('Submitting application:', application);
      const result = await accessRequestService?.create(application);
      console.log('Application submitted successfully:', result);
      
      setIsSubmitting(false);
      setIsSubmitted(true);
    } catch (error) {
      console.error('Error submitting application:', error);
      setErrors({ submit: error?.message || 'Failed to submit application. Please try again.' });
      setIsSubmitting(false);
    }
  };

  const handleReturnHome = () => {
    navigate('/marketing-landing');
  };

  const renderStepContent = () => {
    if (step === 1) {
      return (
        <div className="space-y-6">
          <div className="text-center mb-8">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-semibold text-[#111827] mb-3">
              {t('accessRequest.title')}
            </h2>
            <p className="text-base md:text-lg text-[#6B7280] max-w-2xl mx-auto">
              {t('accessRequest.subtitle')}
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
            <RoleSelectionCard
              role="candidate"
              title={t('accessRequest.candidateTitle')}
              description={t('accessRequest.candidateDesc')}
              requirements={candidateRequirements}
              isSelected={selectedRole === 'candidate'}
              onSelect={handleRoleSelect}
            />
            <RoleSelectionCard
              role="employer"
              title={t('accessRequest.employerTitle')}
              description={t('accessRequest.employerDesc')}
              requirements={employerRequirements}
              isSelected={selectedRole === 'employer'}
              onSelect={handleRoleSelect}
            />
          </div>
          {errors?.role && (
            <div className="bg-[#FEE2E2] border border-[#EF4444] rounded-xl p-4 flex items-start gap-3">
              <span className="text-[#EF4444] text-sm">{errors?.role}</span>
            </div>
          )}
        </div>
      );
    }

    if (step === 2) {
      return selectedRole === 'candidate' ? (
        <CandidateQualificationForm
          formData={formData}
          errors={errors}
          onChange={handleFormChange}
        />
      ) : (
        <EmployerVerificationForm
          formData={formData}
          errors={errors}
          onChange={handleFormChange}
        />
      );
    }

    if (step === 3 && selectedRole === 'employer') {
      return (
        <EmployerTermsOfService
          formData={formData}
          errors={errors}
          onChange={handleFormChange}
        />
      );
    }

    if ((step === 3 && selectedRole === 'candidate') || (step === 4 && selectedRole === 'employer')) {
      return (
        <div className="space-y-6">
          <div>
            <h3 className="text-xl md:text-2xl font-semibold text-[#111827] mb-2">
              Review Your Application
            </h3>
            <p className="text-sm md:text-base text-[#6B7280]">
              Please review your information before submitting
            </p>
          </div>
          <div className="bg-white border border-[#E5E7EB] rounded-2xl p-6 md:p-8 space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <span className="text-xs md:text-sm text-[#6B7280] block mb-1">
                  Full Name
                </span>
                <span className="text-sm md:text-base text-[#111827] font-medium">
                  {formData?.fullName}
                </span>
              </div>
              <div>
                <span className="text-xs md:text-sm text-[#6B7280] block mb-1">
                  Email Address
                </span>
                <span className="text-sm md:text-base text-[#111827] font-medium">
                  {formData?.email}
                </span>
              </div>
            </div>

            {selectedRole === 'candidate' ? (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <span className="text-xs md:text-sm text-[#6B7280] block mb-1">
                      Experience
                    </span>
                    <span className="text-sm md:text-base text-[#111827] font-medium">
                      {formData?.experience} years
                    </span>
                  </div>
                  <div>
                    <span className="text-xs md:text-sm text-[#6B7280] block mb-1">
                      Industry
                    </span>
                    <span className="text-sm md:text-base text-[#111827] font-medium capitalize">
                      {formData?.industry}
                    </span>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <span className="text-xs md:text-sm text-[#6B7280] block mb-1">
                      Seniority Level
                    </span>
                    <span className="text-sm md:text-base text-[#111827] font-medium capitalize">
                      {formData?.seniority}
                    </span>
                  </div>
                  <div>
                    <span className="text-xs md:text-sm text-[#6B7280] block mb-1">
                      Language
                    </span>
                    <span className="text-sm md:text-base text-[#111827] font-medium capitalize">
                      {formData?.language}
                    </span>
                  </div>
                </div>
              </>
            ) : (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <span className="text-xs md:text-sm text-[#6B7280] block mb-1">
                      Company Name
                    </span>
                    <span className="text-sm md:text-base text-[#111827] font-medium">
                      {formData?.companyName}
                    </span>
                  </div>
                  <div>
                    <span className="text-xs md:text-sm text-[#6B7280] block mb-1">
                      Job Title
                    </span>
                    <span className="text-sm md:text-base text-[#111827] font-medium">
                      {formData?.jobTitle}
                    </span>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <span className="text-xs md:text-sm text-[#6B7280] block mb-1">
                      Company Size
                    </span>
                    <span className="text-sm md:text-base text-[#111827] font-medium">
                      {formData?.companySize} employees
                    </span>
                  </div>
                  <div>
                    <span className="text-xs md:text-sm text-[#6B7280] block mb-1">
                      Industry
                    </span>
                    <span className="text-sm md:text-base text-[#111827] font-medium capitalize">
                      {formData?.industry}
                    </span>
                  </div>
                </div>
              </>
            )}
          </div>
          <div className="bg-[#FEF3C7] border border-[#F59E0B] rounded-xl p-4 md:p-6">
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 rounded-md bg-[#F59E0B] flex items-center justify-center flex-shrink-0">
                <span className="text-white text-sm font-bold">!</span>
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="text-sm md:text-base font-medium text-[#111827] mb-1">
                  Before You Submit
                </h4>
                <p className="text-xs md:text-sm text-[#6B7280]">
                  Please ensure all information is accurate. Applications are manually reviewed
                  and cannot be edited after submission.
                </p>
              </div>
            </div>
          </div>
        </div>
      );
    }
  };

  return (
    <div className="min-h-screen bg-white pt-20">
      <PublicHeader />
      {!isSubmitted && <StepIndicator steps={selectedRole === 'employer' ? steps : steps} currentStep={step} />}
      <main className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-8 md:py-12">
        {isSubmitted ? (
          <SuccessConfirmation 
            selectedRole={selectedRole} 
            onReturnHome={handleReturnHome} 
          />
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
            <div className="lg:col-span-2">
              <div className="bg-white rounded-2xl border border-[#E5E7EB] p-6 md:p-8">
                {renderStepContent()}

                <div className="flex flex-col sm:flex-row gap-3 mt-8 pt-6 border-t border-[#E5E7EB]">
                  {step > 1 && (
                    <Button
                      variant="outline"
                      size="lg"
                      onClick={handleBack}
                      iconName="ArrowLeft"
                      iconPosition="left"
                      className="sm:w-auto"
                    >
                      Back
                    </Button>
                  )}
                  {step < steps?.length ? (
                    <Button
                      variant="default"
                      size="lg"
                      onClick={handleNext}
                      iconName="ArrowRight"
                      iconPosition="right"
                      fullWidth={step === 1}
                      className="sm:ml-auto"
                    >
                      Continue
                    </Button>
                  ) : (
                    <Button
                      variant="default"
                      size="lg"
                      onClick={handleSubmit}
                      iconName="Send"
                      iconPosition="right"
                      className="sm:ml-auto"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? 'Submitting...' : 'Submit Application'}
                    </Button>
                  )}
                </div>
                {errors?.submit && (
                  <div className="bg-[#FEE2E2] border border-[#EF4444] rounded-xl p-4 flex items-start gap-3 mt-4">
                    <div className="w-6 h-6 rounded-md bg-[#EF4444] flex items-center justify-center flex-shrink-0">
                      <span className="text-white text-sm font-bold">!</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="text-sm md:text-base font-medium text-[#111827] mb-1">
                        Submission Error
                      </h4>
                      <p className="text-xs md:text-sm text-[#6B7280]">
                        {errors?.submit}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="lg:col-span-1">
              <div className="lg:sticky lg:top-24">
                <ApplicationStatusPanel
                  selectedRole={selectedRole}
                  currentStep={step}
                  totalSteps={selectedRole === 'employer' ? steps?.length : steps?.length}
                  email={formData?.email}
                />
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default AccessRequest;